/**
 * Manager One-Time Jobs List Component
 * Shows all one-time jobs relevant to this manager:
 *   - Jobs assigned to the manager (pending staff assignment)
 *   - Jobs assigned directly to any of their staff members
 */

import { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { OneTimeJob, UserProfile } from '../../../../shared/types';
import { managerAssignOneTimeJobToStaff, removeStaffFromJob, ONE_TIME_JOBS_COLLECTION } from '../../services/oneTimeJob.service';
import { getManagerStaff } from '../../services/user.service';
import { convertDocumentTimestamps, normalizeCompanyId } from '@shared/utils/firestore';
import { ErrorMessage } from '../ErrorMessage';
import { ItemCard } from '../ItemCard';
import { EMPTY_STATE_STYLE } from '../../styles/components';

interface ManagerJobsListProps {
  managerId: string;
  managerProfile?: UserProfile;
  refreshTrigger?: number;
  companyTimezone?: string;
}

export function ManagerJobsList({ managerId, managerProfile, refreshTrigger, companyTimezone }: ManagerJobsListProps) {
  const [oneTimeJobs, setOneTimeJobs] = useState<OneTimeJob[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigningOneTimeJobId, setAssigningOneTimeJobId] = useState<string | null>(null);

  // Use refs to hold latest snapshot results from each listener so we can merge them
  const managerJobsRef = useRef<Map<string, OneTimeJob>>(new Map());
  const staffJobsRef = useRef<Map<string, OneTimeJob>>(new Map());

  const companyId = normalizeCompanyId(managerProfile?.companyId);

  const mergeAndSet = () => {
    const merged = new Map([...managerJobsRef.current, ...staffJobsRef.current]);
    const sorted = Array.from(merged.values()).sort(
      (a, b) => b.scheduledStart.getTime() - a.scheduledStart.getTime()
    );
    setOneTimeJobs(sorted);
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    managerJobsRef.current = new Map();
    staffJobsRef.current = new Map();

    if (!companyId) {
      setError('No company assigned to this manager profile.');
      setLoading(false);
      return;
    }

    // Query 1: jobs assigned to this manager (pending staff assignment)
    const managerQuery = query(
      collection(db, ONE_TIME_JOBS_COLLECTION),
      where('assignedManagerId', '==', managerId),
      where('assignmentStatus', '==', 'assigned_to_manager'),
      orderBy('scheduledStart', 'desc')
    );

    const unsubManager = onSnapshot(
      managerQuery,
      (snapshot) => {
        managerJobsRef.current = new Map(
          snapshot.docs.map((doc) => {
            const job = { id: doc.id, ...convertDocumentTimestamps<OneTimeJob>(doc.data()) } as OneTimeJob;
            return [job.id, job];
          })
        );
        mergeAndSet();
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load one-time jobs');
        setLoading(false);
      }
    );

    // Load staff, then set up Query 2: jobs assigned directly to any staff member
    let unsubStaff: (() => void) | null = null;

    getManagerStaff(managerId)
      .then((staffData) => {
        setStaff(staffData);

        if (staffData.length === 0) return;

        // Query by assignedManagerId — matches Firestore rule (no get() call needed)
        const staffQuery = query(
          collection(db, ONE_TIME_JOBS_COLLECTION),
          where('assignedManagerId', '==', managerId),
          where('assignmentStatus', '==', 'assigned_to_staff'),
          orderBy('scheduledStart', 'desc')
        );

        unsubStaff = onSnapshot(
          staffQuery,
          (snapshot) => {
            staffJobsRef.current = new Map(
              snapshot.docs.map((doc) => {
                const job = { id: doc.id, ...convertDocumentTimestamps<OneTimeJob>(doc.data()) } as OneTimeJob;
                return [job.id, job];
              })
            );
            mergeAndSet();
          },
          (err) => {
            setError(err.message || 'Failed to load staff jobs');
          }
        );
      })
      .catch((err) => {
        setError((err as Error).message || 'Failed to load staff');
      });

    return () => {
      unsubManager();
      unsubStaff?.();
    };
  }, [managerId, companyId, refreshTrigger]);

  const handleAssignToStaff = async (oneTimeJobId: string, staffId: string) => {
    setAssigningOneTimeJobId(oneTimeJobId);
    try {
      await managerAssignOneTimeJobToStaff(oneTimeJobId, staffId, managerId);
    } catch (err) {
      setError((err as Error).message || 'Failed to assign one-time job to staff');
    } finally {
      setAssigningOneTimeJobId(null);
    }
  };

  const handleUnassignStaff = async (oneTimeJobId: string, staffId: string) => {
    setAssigningOneTimeJobId(oneTimeJobId);
    try {
      await removeStaffFromJob(oneTimeJobId, companyId!, staffId);
    } catch (err) {
      setError((err as Error).message || 'Failed to remove staff from job');
    } finally {
      setAssigningOneTimeJobId(null);
    }
  };

  const handleUnassignMyStaff = async (oneTimeJobId: string, staffIds: string[]) => {
    setAssigningOneTimeJobId(oneTimeJobId);
    try {
      await Promise.all(staffIds.map((staffId) => removeStaffFromJob(oneTimeJobId, companyId!, staffId)));
    } catch (err) {
      setError((err as Error).message || 'Failed to unassign staff from job');
    } finally {
      setAssigningOneTimeJobId(null);
    }
  };

  const getUserName = (userId: string): string => {
    if (managerProfile && userId === managerId) {
      return `${managerProfile.firstName} ${managerProfile.lastName}`;
    }
    const staffMember = staff.find((s) => s.uid === userId);
    return staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'Unknown';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading one-time jobs...</div>;
  }

  return (
    <div>
      {error && <ErrorMessage message={error} />}

      {oneTimeJobs.length === 0 ? (
        <div style={EMPTY_STATE_STYLE}>
          No one-time jobs assigned to you yet. Your company administrator will assign one-time jobs to you.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {oneTimeJobs.map((oneTimeJob) => (
            <ItemCard
              key={oneTimeJob.id}
              item={oneTimeJob}
              viewerRole="manager"
              availableUsers={staff}
              onAssignToStaff={handleAssignToStaff}
              onUnassignStaff={handleUnassignStaff}
              onUnassignMyStaff={handleUnassignMyStaff}
              isAssigning={assigningOneTimeJobId === oneTimeJob.id}
              getUserName={getUserName}
              companyTimezone={companyTimezone}
            />
          ))}
        </div>
      )}
    </div>
  );
}
